import { FunnelConfig, OUT_STAGE } from '../view-project-new/funnel/funnel.logic';

export interface FunnelBand {
  id: string;
  name: string;
  /** P0 is the goal stage, normally the last stage. */
  pri: number;
  count: number;
  share: number;
  /** Band width in percent: widest for the lowest priority, 66 for the goal. */
  width: number;
  outcomes: { status: string; count: number }[];
}

export interface FunnelViewModel {
  /** Lowest priority first, so the widest band is on top. */
  bands: FunnelBand[];
  /** Outcomes that end the journey, if the funnel maps any. */
  leftFunnel: { status: string; count: number }[];
  /** Statuses with leads that the funnel does not place in a stage. */
  unplaced: { status: string; count: number }[];
  total: number;
}

const key = (name: string) => String(name || '').trim().toLowerCase();

/**
 * Groups the outcome counts of the leads screen by funnel stage. Matching is by
 * outcome name, ignoring case, because statuses come back as typed by the agent.
 */
export function buildFunnelView(config: FunnelConfig, statusCounts: { [status: string]: number }): FunnelViewModel {
  const counts = statusCounts || {};
  const stageOf: { [name: string]: string } = {};
  Object.keys(config.outcome_map || {}).forEach(name => (stageOf[key(name)] = config.outcome_map[name].stage));

  const byStage: { [id: string]: { status: string; count: number }[] } = {};
  const leftFunnel: { status: string; count: number }[] = [];
  const unplaced: { status: string; count: number }[] = [];
  Object.keys(counts).forEach(status => {
    const count = Number(counts[status]) || 0;
    if (count <= 0) { return; }
    const stage = stageOf[key(status)];
    const item = { status, count };
    if (stage === OUT_STAGE) {
      leftFunnel.push(item);
    } else if (stage && config.stages.some(s => s.id === stage)) {
      (byStage[stage] = byStage[stage] || []).push(item);
    } else {
      unplaced.push(item);
    }
  });
  const bySize = (a: { count: number }, b: { count: number }) => b.count - a.count;
  [leftFunnel, unplaced].forEach(list => list.sort(bySize));

  const sums = config.stages.map(s => (byStage[s.id] || []).reduce((n, o) => n + o.count, 0));
  const total = sums.reduce((n, c) => n + c, 0) + leftFunnel.reduce((n, o) => n + o.count, 0) + unplaced.reduce((n, o) => n + o.count, 0);
  const last = config.stages.length - 1;
  const bands = config.stages.map((s, i) => ({
    id: s.id,
    name: s.name,
    pri: s.pri,
    count: sums[i],
    share: total > 0 ? Math.round((sums[i] / total) * 1000) / 10 : 0,
    width: Math.round(100 - (last > 0 ? (i / last) * 34 : 0)),
    outcomes: (byStage[s.id] || []).sort(bySize)
  }));
  return { bands, leftFunnel, unplaced, total };
}
